import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { batchAPI } from '../services/api';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { ArrowLeft, Package, Image as ImageIcon, CheckCircle, XCircle, TrendingUp, Download, X } from 'lucide-react';

const BatchList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [stats, setStats] = useState({ totalBatches: 0, totalImages: 0, passedImages: 0, failedImages: 0 });
  const [batchProgress, setBatchProgress] = useState({});
  const batchesRef = useRef([]);

  // CSV Download modal state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFromDate, setCsvFromDate] = useState('');
  const [csvToDate, setCsvToDate] = useState('');

  // Validation Report modal state
  const [showValidationReportModal, setShowValidationReportModal] = useState(false);
  const [completedBatches, setCompletedBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState(new Set());
  const [reportDownloading, setReportDownloading] = useState(false);

  useEffect(() => { batchesRef.current = batches; }, [batches]);

  useEffect(() => { fetchBatches(activeTab); setCurrentPage(1); }, [activeTab]); // eslint-disable-line

  // Poll for active batches
  useEffect(() => {
    const interval = setInterval(async () => {
      const current = batchesRef.current;
      const processing = current.filter(b => b.status === 'processing');
      const pending = current.filter(b => ['uploading', 'uploaded'].includes(b.status));
      if (processing.length === 0 && pending.length === 0) return;

      for (const batch of processing) {
        try {
          const res = await batchAPI.getValidationProgress(batch.batch_id);
          setBatchProgress(prev => ({ ...prev, [batch.batch_id]: res.data }));
          if (res.data.current_status === 'waiting' || res.data.current_status === 'errors') {
            fetchBatches(activeTab);
          }
        } catch (e) { console.error(e); }
      }
      if (pending.length > 0) fetchBatches(activeTab);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab]); // eslint-disable-line

  const fetchBatches = async (filterTab = 'all') => {
    try {
      setLoading(true);
      const response = await batchAPI.list(0, 100, filterTab);
      const list = response.data;
      setBatches(list);
      setStats({
        totalBatches: list.length,
        totalImages: list.reduce((s, b) => s + b.total_images, 0),
        passedImages: list.reduce((s, b) => s + b.passed_images, 0),
        failedImages: list.reduce((s, b) => s + b.failed_images, 0),
      });
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId, batchName) => {
    if (!window.confirm(`Are you sure you want to delete "${batchName}"?\n\nThis action cannot be undone.`)) return;
    try {
      await batchAPI.delete(batchId);
      fetchBatches(activeTab);
      alert(`Batch "${batchName}" deleted successfully.`);
    } catch (error) {
      alert(`Failed to delete batch "${batchName}".`);
    }
  };

  const openValidationReportModal = async () => {
    try {
      const response = await batchAPI.list(0, 500, 'all');
      const all = response.data?.batches || response.data || [];
      setCompletedBatches(all.filter(b => ['waiting', 'errors', 'approved', 'rejected'].includes(b.status)));
      setSelectedBatchIds(new Set());
      setShowValidationReportModal(true);
    } catch (e) {
      alert('Failed to load batches: ' + (e.response?.data?.detail || e.message));
    }
  };

  const toggleBatchSelection = (batchId) => {
    setSelectedBatchIds(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId); else next.add(batchId);
      return next;
    });
  };

  const handleDownloadValidationReport = async () => {
    if (selectedBatchIds.size === 0) { alert('Please select at least one batch.'); return; }
    try {
      setReportDownloading(true);
      await batchAPI.downloadValidationReport(Array.from(selectedBatchIds));
      setShowValidationReportModal(false);
    } catch (e) {
      alert('Failed to download report: ' + (e.response?.data?.detail || e.message));
    } finally {
      setReportDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!csvFromDate || !csvToDate) { alert('Please select both from and to dates.'); return; }
    try {
      const response = await batchAPI.getReport(csvFromDate, csvToDate);
      const data = response.data;
      if (data.length === 0) { alert('No batches found in the selected date range.'); return; }
      const rows = [
        ['Batch Name', 'Total Images', 'Uploaded Date & Time'],
        ...data.map(b => [b.batch_name, b.total_images, b.uploaded_at])
      ];
      const csvContent = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batches_${csvFromDate}_to_${csvToDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowCsvModal(false);
    } catch (error) {
      alert('Failed to download report: ' + (error.response?.data?.detail || error.message));
    }
  };

  const totalPages = Math.ceil(batches.length / PAGE_SIZE);
  const paginatedBatches = batches.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const tabs = [
    { key: 'all', label: 'All Batches', icon: '📦' },
    { key: 'passed', label: 'Validation Passed', icon: '✅' },
    { key: 'failed', label: 'Validation Failed', icon: '❌' },
    { key: 'rejected', label: 'Auditor Rejected', icon: '🚫' },
    { key: 'approved', label: 'Approved', icon: '✔️' },
  ];

  const getStatusBadgeStyle = (status) => {
    const map = {
      incoming: { bg: 'hsl(210 90% 55% / 0.1)', color: 'hsl(210 90% 55%)' },
      processing: { bg: 'hsl(38 92% 50% / 0.1)', color: 'hsl(38 92% 50%)' },
      errors: { bg: 'hsl(0 84% 60% / 0.1)', color: 'hsl(0 84% 60%)' },
      waiting: { bg: 'hsl(270 70% 60% / 0.1)', color: 'hsl(270 70% 60%)' },
      approved: { bg: 'hsl(152 60% 40% / 0.1)', color: 'hsl(152 60% 40%)' },
      rejected: { bg: 'hsl(0 84% 60% / 0.1)', color: 'hsl(0 84% 60%)' },
    };
    return map[status] || map.incoming;
  };

  const statusLabel = (status) => ({
    uploading: 'Uploading', uploaded: 'In Queue', processing: 'Validating',
    waiting: 'Completed', errors: 'Errors', approved: 'Approved',
    rejected: 'Rejected', incoming: 'Incoming',
  })[status] || status;

  const styles = `
    .batchlist-container { height: 100vh; background: hsl(var(--background)); display: flex; overflow: hidden; }

    .main-content {
      flex: 1;
      margin-left: ${collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)'};
      margin-top: var(--header-height);
      padding: var(--page-padding);
      transition: margin-left 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      overflow-y: auto;
      height: calc(100vh - var(--header-height));
    }

    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
    .page-title { font-size: 28px; font-weight: 800; color: hsl(var(--foreground)); margin-bottom: 4px; }
    .page-subtitle { font-size: 14px; color: hsl(var(--muted-foreground)); }

    .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 24px; }

    .toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; }

    .tabs-row { display: flex; gap: 8px; flex-wrap: wrap; }

    .tab-btn {
      padding: 6px 14px; border-radius: var(--radius-md); border: 1px solid hsl(var(--border));
      background: transparent; color: hsl(var(--muted-foreground)); font-size: 13px; font-weight: 600;
      cursor: pointer; font-family: inherit; transition: all 0.2s;
    }
    .tab-btn.active { background: hsl(var(--primary)); color: white; border-color: hsl(var(--primary)); }

    .download-btns { display: flex; gap: 8px; }

    .batches-card { background: var(--gradient-card); border-radius: var(--radius-lg); border: 1px solid hsl(var(--border)); box-shadow: var(--shadow-card); overflow: hidden; }
    .batches-list { padding: 20px; }

    .batch-item { background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: var(--radius-md); padding: 20px; margin-bottom: 16px; transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1); }
    .batch-item:last-child { margin-bottom: 0; }
    .batch-item:hover { box-shadow: var(--shadow-card-hover); transform: translateY(-2px); border-color: hsl(var(--primary)); }

    .batch-header { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
    .batch-name { font-size: 16px; font-weight: 600; color: hsl(var(--foreground)); margin-bottom: 4px; }
    .batch-date { font-size: 12px; color: hsl(var(--muted-foreground)); }

    .batch-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 16px; padding: 16px; background: hsl(var(--secondary) / 0.5); border-radius: var(--radius-md); }
    .mini-stat { text-align: center; }
    .mini-stat-label { font-size: 11px; font-weight: 600; color: hsl(var(--muted-foreground)); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    .mini-stat-value { font-size: 20px; font-weight: 700; }

    .batch-actions { display: flex; gap: 8px; justify-content: flex-end; }
    .action-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: var(--radius-md); font-size: 13px; font-weight: 600; border: none; cursor: pointer; transition: all 0.2s; font-family: inherit; }
    .btn-view { background: hsl(var(--info) / 0.1); color: hsl(var(--info)); }
    .btn-view:hover { background: hsl(var(--info)); color: white; }
    .btn-delete { background: hsl(var(--destructive) / 0.1); color: hsl(var(--destructive)); }
    .btn-delete:hover { background: hsl(var(--destructive)); color: white; }

    .empty-state { text-align: center; padding: 60px 20px; }
    .empty-icon { font-size: 48px; opacity: 0.3; margin-bottom: 16px; }
    .empty-text { font-size: 14px; color: hsl(var(--muted-foreground)); }

    @media (max-width: 768px) {
      .main-content { margin-left: 0; }
      .batch-stats { grid-template-columns: repeat(2, 1fr); }
      .stats-grid { grid-template-columns: 1fr 1fr; }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="batchlist-container">
        <Sidebar user={user} logout={logout} collapsed={collapsed} setCollapsed={setCollapsed} />
        <Navbar user={user} collapsed={collapsed} />

        <motion.div className="main-content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>

          {/* Header */}
          <div className="page-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', padding: '4px' }}>
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="page-title">Batches</h1>
                <p className="page-subtitle">View and manage all your batches</p>
              </div>
            </div>
            {user?.is_active !== false && (
              <button className="btn-primary" onClick={() => navigate('/batch/create')}>+ New Batch</button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            {[
              { icon: <Package size={20} />, value: stats.totalBatches, label: 'Total Batches', color: 'hsl(var(--foreground))', sub: null },
              { icon: <ImageIcon size={20} />, value: stats.totalImages, label: 'Total Images', color: 'hsl(var(--foreground))', sub: null },
              { icon: <CheckCircle size={20} />, value: stats.passedImages, label: 'Passed Images', color: 'hsl(var(--success))', sub: `${stats.totalImages > 0 ? Math.round((stats.passedImages / stats.totalImages) * 100) : 0}% Success Rate` },
              { icon: <XCircle size={20} />, value: stats.failedImages, label: 'Failed Images', color: 'hsl(var(--destructive))', sub: 'Needs attention' },
            ].map((s, i) => (
              <div key={i} className="stat-card">
                <div className="icon-container" style={s.label === 'Failed Images' ? { background: 'hsl(var(--destructive) / 0.1)', color: 'hsl(var(--destructive))' } : {}}>
                  {s.icon}
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: '800', color: s.color, margin: '12px 0 4px' }}>
                  {loading ? '—' : s.value}
                </h3>
                <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
                {s.sub && (
                  <div style={{ fontSize: '12px', color: s.color, marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {s.label === 'Passed Images' && <TrendingUp size={12} />}
                    {s.sub}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Toolbar: Tabs + Download buttons */}
          <div className="toolbar">
            <div className="tabs-row">
              {tabs.map(tab => (
                <button key={tab.key} className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
            <div className="download-btns">
              <button
                onClick={() => setShowCsvModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'hsl(var(--primary))', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Download size={14} /> Download CSV
              </button>
              <button
                onClick={openValidationReportModal}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'linear-gradient(135deg, hsl(142 76% 36%) 0%, hsl(142 76% 28%) 100%)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Download size={14} /> Validation Report
              </button>
            </div>
          </div>

          {/* Batch List */}
          <div className="batches-card">
            <div className="batches-list">
              {loading ? (
                <div className="empty-state"><div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>Loading batches...</div></div>
              ) : batches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <div className="empty-text">No batches found.</div>
                </div>
              ) : (
                paginatedBatches.map((batch, index) => {
                  const badgeStyle = getStatusBadgeStyle(batch.status);
                  const progress = batchProgress[batch.batch_id];
                  return (
                    <motion.div key={batch.batch_id} className="batch-item" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
                      <div className="batch-header">
                        <div>
                          <h3 className="batch-name">{batch.batch_name}</h3>
                          <p className="batch-date">Uploaded: {new Date(batch.uploaded_at).toLocaleString()}</p>
                          {/* Validation status labels */}
                          {!['processing', 'uploading', 'uploaded', 'incoming'].includes(batch.status) && (
                            <div style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: batch.failed_images === 0 ? 'hsl(var(--success))' : 'hsl(var(--destructive))' }}>
                                {batch.failed_images === 0 ? '✅ Validation Passed' : '❌ Validation Failed'}
                              </span>
                              {batch.auditor_status === 'rejected' && (
                                <span style={{ fontSize: '12px', fontWeight: '600', color: 'hsl(var(--destructive))' }}>🚫 Auditor Rejected</span>
                              )}
                            </div>
                          )}
                          {/* Metadata errors */}
                          {(batch.metadata_validation_failures || batch.artifact_count_match === false || batch.capture_id_match === false) && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 'var(--radius-md)', fontSize: '12px', color: 'hsl(var(--destructive))' }}>
                              ⚠️ <strong>GroupMetadata Validation Failed:</strong> {batch.metadata_validation_failures}
                              {batch.artifact_count_match === false && ' • Artifact count mismatch'}
                              {batch.capture_id_match === false && ' • Capture ID mismatch'}
                            </div>
                          )}
                          {batch.has_sequence_error && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 'var(--radius-md)', fontSize: '12px', color: 'hsl(var(--destructive))' }}>
                              ⚠️ <strong>Image Sequence Error:</strong> Image numbers are not in sequence. Re-upload this batch.
                            </div>
                          )}
                          {batch.rejection_reason && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 'var(--radius-md)', fontSize: '12px', color: 'hsl(var(--destructive))' }}>
                              🚫 <strong>Rejection Reason:</strong> {batch.rejection_reason}
                            </div>
                          )}
                          {/* Progress */}
                          {batch.status === 'processing' && progress && (
                            <div style={{ marginTop: '10px', padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '6px' }}>⚙️ Validating Images...</div>
                              <div style={{ background: '#dbeafe', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                                <div style={{ background: '#3b82f6', height: '100%', width: `${progress.progress_percentage}%`, transition: 'width 0.5s' }} />
                              </div>
                              <div style={{ fontSize: '11px', color: '#1e40af', marginTop: '4px' }}>
                                {progress.processed_images}/{progress.total_images} processed
                              </div>
                            </div>
                          )}
                          {batch.status === 'uploaded' && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '12px', color: '#0c4a6e' }}>
                              ⏳ In queue — validation begins automatically within 1 minute
                            </div>
                          )}
                        </div>
                        <span className="badge" style={{ background: badgeStyle.bg, color: badgeStyle.color, flexShrink: 0 }}>
                          {statusLabel(batch.status)}
                        </span>
                      </div>

                      <div className="batch-stats">
                        <div className="mini-stat"><div className="mini-stat-label">Total</div><div className="mini-stat-value" style={{ color: 'hsl(var(--foreground))' }}>{batch.total_images}</div></div>
                        <div className="mini-stat"><div className="mini-stat-label">Passed</div><div className="mini-stat-value" style={{ color: 'hsl(var(--success))' }}>{batch.passed_images}</div></div>
                        <div className="mini-stat"><div className="mini-stat-label">Failed</div><div className="mini-stat-value" style={{ color: 'hsl(var(--destructive))' }}>{batch.failed_images}</div></div>
                        <div className="mini-stat"><div className="mini-stat-label">Success Rate</div><div className="mini-stat-value" style={{ color: 'hsl(var(--info))' }}>{batch.total_images > 0 ? Math.round((batch.passed_images / batch.total_images) * 100) : 0}%</div></div>
                      </div>

                      <div className="batch-actions">
                        <button className="action-btn btn-view" onClick={() => navigate(`/batch/${batch.batch_id}`)}>👁️ View Details</button>
                        <button className="action-btn btn-delete" onClick={() => handleDeleteBatch(batch.batch_id, batch.batch_name)}>🗑️ Delete</button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderTop: '1px solid hsl(var(--border))' }}>
                <span style={{ fontSize: '13px', color: 'hsl(var(--muted-foreground))' }}>
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, batches.length)} of {batches.length} batches
                </span>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: '13px', fontWeight: '600', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.4 : 1, fontFamily: 'inherit' }}
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid hsl(var(--border))', background: currentPage === page ? 'hsl(var(--primary))' : 'hsl(var(--card))', color: currentPage === page ? 'white' : 'hsl(var(--foreground))', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: '13px', fontWeight: '600', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.4 : 1, fontFamily: 'inherit' }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* CSV Download Modal */}
      {showCsvModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'hsl(var(--card))', borderRadius: 'var(--radius-lg)', padding: '32px', width: '400px', boxShadow: 'var(--shadow-card)', border: '1px solid hsl(var(--border))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--foreground))', margin: 0 }}>Download Batch Report</h2>
              <button onClick={() => setShowCsvModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }}><X size={20} /></button>
            </div>
            {[['From Date', csvFromDate, setCsvFromDate], ['To Date', csvToDate, setCsvToDate]].map(([label, val, setter]) => (
              <div key={label} style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'hsl(var(--foreground))', marginBottom: '6px' }}>{label}</label>
                <input type="date" value={val} onChange={e => setter(e.target.value)} style={{ width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius-md)', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button onClick={() => setShowCsvModal(false)} style={{ flex: 1, padding: '10px', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius-md)', background: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDownloadCsv} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 'var(--radius-md)', background: 'hsl(var(--primary))', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Download</button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Report Modal */}
      {showValidationReportModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'hsl(var(--card))', borderRadius: 'var(--radius-lg)', padding: '32px', width: '460px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-card)', border: '1px solid hsl(var(--border))' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'hsl(var(--foreground))', margin: 0 }}>Download Validation Report</h2>
              <button onClick={() => setShowValidationReportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: 'hsl(var(--muted-foreground))', marginBottom: '16px' }}>Select completed batches to download their validation report.</p>
            {completedBatches.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))', padding: '24px 0' }}>No completed batches found.</p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'hsl(var(--muted-foreground))' }}>{selectedBatchIds.size} of {completedBatches.length} selected</span>
                  <button onClick={() => setSelectedBatchIds(selectedBatchIds.size === completedBatches.length ? new Set() : new Set(completedBatches.map(b => b.batch_id)))} style={{ fontSize: '13px', color: 'hsl(var(--primary))', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
                    {selectedBatchIds.size === completedBatches.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1, border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius-md)', marginBottom: '20px' }}>
                  {completedBatches.map(batch => (
                    <label key={batch.batch_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid hsl(var(--border))', background: selectedBatchIds.has(batch.batch_id) ? 'hsl(var(--primary) / 0.06)' : 'transparent' }}>
                      <input type="checkbox" checked={selectedBatchIds.has(batch.batch_id)} onChange={() => toggleBatchSelection(batch.batch_id)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'hsl(var(--foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{batch.batch_name}</div>
                        <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))' }}>{batch.total_images} images • {new Date(batch.uploaded_at).toLocaleDateString()}</div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px', background: batch.status === 'waiting' ? 'hsl(var(--success) / 0.15)' : 'hsl(var(--destructive) / 0.15)', color: batch.status === 'waiting' ? 'hsl(var(--success))' : 'hsl(var(--destructive))', textTransform: 'uppercase' }}>{batch.status}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowValidationReportModal(false)} style={{ flex: 1, padding: '10px', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius-md)', background: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDownloadValidationReport} disabled={reportDownloading || selectedBatchIds.size === 0} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, hsl(142 76% 36%) 0%, hsl(142 76% 28%) 100%)', color: 'white', fontSize: '14px', fontWeight: '600', cursor: selectedBatchIds.size === 0 ? 'not-allowed' : 'pointer', opacity: selectedBatchIds.size === 0 ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Download size={15} />{reportDownloading ? 'Downloading...' : `Download (${selectedBatchIds.size})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BatchList;
